import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Info,
  MessageCircle,
  QrCode,
  RefreshCw,
  Send,
  Wifi,
  WifiOff,
} from "lucide-react";
import { ROUTES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { whatsappApi, type WhatsappStatusDto } from "@/services/api";
import { cn } from "@/utils/cn";

const STATUS_LABEL: Record<string, string> = {
  connected: "Conectado",
  connecting: "Aguardando leitura do QR Code",
  disconnected: "Desconectado",
  not_configured: "Não configurado",
  token_invalid: "Token expirado — reconecte",
};

export function WhatsappPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<WhatsappStatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [number, setNumber] = useState("");
  const [text, setText] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await whatsappApi.status();
      setStatus(res.data);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Falha ao consultar status do WhatsApp.";
      setError(msg);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  const handleSetup = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      await whatsappApi.setup();
      await refresh({ silent: true });
      setSuccess("Instância criada com sucesso.");
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Falha ao criar instância.";
      setError(msg);
    } finally {
      setConnecting(false);
    }
  }, [refresh]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await whatsappApi.connect();
      setStatus(res.data);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Falha ao gerar QR Code.";
      setError(msg);
    } finally {
      setConnecting(false);
    }
  }, []);

  const handleDisconnect = useCallback(async () => {
    setConnecting(true);
    setError(null);
    try {
      await whatsappApi.disconnect();
      await refresh({ silent: true });
      setSuccess("WhatsApp desconectado.");
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Falha ao desconectar.";
      setError(msg);
    } finally {
      setConnecting(false);
    }
  }, [refresh]);

  const handleSend = useCallback(async () => {
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      await whatsappApi.send(number, text);
      setSuccess("Mensagem enviada com sucesso!");
      setText("");
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Falha ao enviar mensagem.";
      setError(msg);
    } finally {
      setSending(false);
    }
  }, [number, text]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    const st = status?.status;
    if (st === "connecting" || (status?.configured && !status?.connected)) {
      pollRef.current = setInterval(() => refresh({ silent: true }), 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [status?.status, status?.configured, status?.connected, refresh]);

  const isConnected = status?.connected === true;
  const statusKey = status?.status ?? (status?.configured ? "disconnected" : "not_configured");
  const statusLabel = STATUS_LABEL[statusKey] ?? statusKey;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.HOME)}
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
          <h1 className="text-lg font-bold">WhatsApp</h1>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => refresh()}
          loading={loading}
          leftIcon={<RefreshCw className="w-4 h-4" />}
          className="ml-auto"
        >
          Atualizar
        </Button>
      </header>

      <div className="flex-1 w-full max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            {success}
          </div>
        )}
        {status?.warning && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {status.warning}
          </div>
        )}

        <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 px-4 py-3 flex gap-3">
          <Info className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700 space-y-1">
            <p className="font-medium text-slate-800">Integração Uazapi</p>
            <p className="text-xs text-slate-600">
              Conecte o número da SEMOG escaneando o QR Code no WhatsApp (Aparelhos conectados).
              Após conectar, use o formulário abaixo para enviar mensagens de texto.
            </p>
          </div>
        </div>

        {/* Status */}
        <Card className="!p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center",
                  isConnected ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400",
                )}
              >
                {isConnected ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{statusLabel}</p>
                {status?.profileName && (
                  <p className="text-xs text-slate-500">{status.profileName}</p>
                )}
                {status?.config?.instanceName && (
                  <p className="text-[11px] text-slate-400">
                    Instância: {status.config.instanceName}
                  </p>
                )}
              </div>
            </div>
            {isConnected && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Online
              </span>
            )}
          </div>

          <div className="p-5 space-y-4">
            {!status?.configured ? (
              <div className="text-center space-y-4 py-4">
                <p className="text-sm text-slate-600">Nenhuma instância configurada ainda.</p>
                <Button onClick={handleSetup} loading={connecting}>
                  Criar instância SEMOG
                </Button>
              </div>
            ) : (
              <>
                {!isConnected && (
                  <div className="flex flex-col items-center gap-4">
                    {status?.qrcode ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                          <QrCode className="w-4 h-4" />
                          Escaneie o QR Code
                        </div>
                        <img
                          src={status.qrcode}
                          alt="QR Code WhatsApp"
                          className="w-64 h-64 rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
                        />
                        <p className="text-xs text-slate-500 text-center max-w-xs">
                          Abra o WhatsApp no celular → Menu → Aparelhos conectados → Conectar aparelho
                        </p>
                      </div>
                    ) : (
                      <Button
                        onClick={handleConnect}
                        loading={connecting}
                        leftIcon={<QrCode className="w-4 h-4" />}
                      >
                        Gerar QR Code
                      </Button>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-center pt-2">
                  {!isConnected && status?.qrcode && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleConnect}
                      loading={connecting}
                      leftIcon={<RefreshCw className="w-4 h-4" />}
                    >
                      Atualizar QR Code
                    </Button>
                  )}
                  {isConnected && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleDisconnect}
                      loading={connecting}
                    >
                      Desconectar
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Envio de mensagem */}
        <Card>
          <h2 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Send className="w-4 h-4 text-brand-600" />
            Enviar mensagem
          </h2>
          <div className="space-y-4">
            <Input
              label="Número (DDI + DDD + número)"
              placeholder="5511999999999"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              hint="Somente dígitos, com código do país (55 para Brasil)"
              disabled={!isConnected}
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Mensagem
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                disabled={!isConnected}
                placeholder={isConnected ? "Digite sua mensagem…" : "Conecte o WhatsApp primeiro"}
                className={cn(
                  "w-full rounded-lg border bg-slate-50 px-3 py-2.5 text-sm text-slate-800",
                  "placeholder:text-slate-400 transition-colors resize-y min-h-[100px]",
                  "focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600",
                  "border-slate-200 hover:border-slate-300",
                  !isConnected && "opacity-60 cursor-not-allowed",
                )}
              />
            </div>
            <Button
              onClick={handleSend}
              loading={sending}
              disabled={!isConnected || !number.trim() || !text.trim()}
              leftIcon={<Send className="w-4 h-4" />}
            >
              Enviar
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
